import React, { useState, useEffect, useRef } from 'react'
import { 
  Plus,
  Users,
  Clock,
  AlertCircle,
  TrendingUp,
  Calendar,
  User,
  FolderOpen
} from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import resourceAllocationService from '../../../services/resourceAllocationService'
import RevisionHistoryModal from '../../../components/RevisionHistoryModal/RevisionHistoryModal'
import '../../../components/RevisionHistoryModal/RevisionHistoryModal.css'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const ResourceAllocationList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    totalAllocations: 0,
    planned: 0,
    active: 0,
    onHold: 0,
    completed: 0,
    cancelled: 0,
    overdue: 0
  })
  const [showRevisionHistory, setShowRevisionHistory] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const [selectedResourceAllocationData, setSelectedResourceAllocationData] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  // Load data from backend
  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [allocationsResponse, statsResponse] = await Promise.all([
        resourceAllocationService.getAllResourceAllocations(),
        resourceAllocationService.getResourceAllocationStats()
      ])

      if (allocationsResponse.success !== false && allocationsResponse.resourceAllocations) {
        setData(allocationsResponse.resourceAllocations || [])
        
        // Show toast if no data
        if ((allocationsResponse.resourceAllocations || []).length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No resource allocations found. Create your first allocation to get started!')
        }
      } else {
        toast.error(allocationsResponse.message || 'Failed to load resource allocations')
      }

      if (statsResponse.success) {
        setStats(statsResponse.data || {})
      }
    } catch (error) {
      console.error('Error loading data:', error)
      if (!error._errorHandled) {
        toast.error('Failed to load resource allocations data')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleAddNew = () => {
    navigate('/projects/resource-allocations/new')
  }

  const handleEdit = (allocation) => {
    const allocationId = allocation._id || allocation.id
    if (!allocationId) {
      toast.error('Resource allocation ID not found')
      return
    }
    navigate(`/projects/resource-allocations/edit/${allocationId}`)
  }

  const handleView = (allocation) => {
    const allocationId = allocation._id || allocation.id
    if (!allocationId) {
      toast.error('Resource allocation ID not found')
      return
    }
    navigate(`/projects/resource-allocations/view/${allocationId}`)
  }

  const handleDelete = (allocation) => {
    setDeleteItem(allocation)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return

    try {
      const allocationId = deleteItem._id || deleteItem.id
      if (!allocationId) {
        toast.error('Resource allocation ID not found')
        return
      }
      const response = await resourceAllocationService.deleteResourceAllocation(allocationId)
      if (response.success) {
        toast.success('Resource allocation deleted successfully')
        loadData() // Reload data
      } else {
        toast.error(response.message || 'Failed to delete resource allocation')
      }
    } catch (error) {
      console.error('Error deleting resource allocation:', error)
      if (!error._errorHandled) {
        toast.error('Failed to delete resource allocation')
      }
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
    }
  }

  const handleRevisionHistory = (allocation) => {
    if (!allocation) {
      toast.error('Resource allocation data not found')
      return
    }
    setSelectedResourceAllocationData(allocation)
    setShowRevisionHistory(true)
  }

  const handleRowSelection = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  const handleSelectionChange = (selectedRows) => {
    setSelectedRows(selectedRows)
  }

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
    const day = String(date.getDate()).padStart(2, '0')
    const month = months[date.getMonth()]
    const year = date.getFullYear()
    return `${day}-${month}-${year}`
  }

  // Define columns for the data table
  const columns = [
    {
      key: 'allocationNumber',
      label: 'Allocation #',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (value, row) => {
        const project = row.project
        if (!project) return <span className="text-gray-500">N/A</span>
        const projectName = typeof project === 'string' 
          ? project 
          : (project.projectName || project.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {projectName}
          </div>
        )
      }
    },
    {
      key: 'employee',
      label: 'Employee',
      sortable: true,
      render: (value, row) => {
        const employee = row.employee
        if (!employee) return <span className="text-gray-500">N/A</span>
        const employeeName = typeof employee === 'string' 
          ? employee 
          : (employee.employeeName || employee.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {employeeName}
          </div>
        )
      }
    },
    {
      key: 'resourceRole',
      label: 'Role',
      sortable: true,
      render: (value, row) => {
        const role = row.resourceRole
        if (!role) return <span className="text-gray-500">N/A</span>
        const roleName = typeof role === 'string' 
          ? role 
          : (role.roleName || role.name || 'N/A')
        return (
          <div className="font-medium text-gray-900">
            {roleName}
          </div>
        )
      }
    },
    {
      key: 'allocationPercentage',
      label: 'Allocation %',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {value || 0}%
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusColors = {
          'Planned': '#6B7280',
          'Active': '#10B981',
          'On Hold': '#F59E0B',
          'Completed': '#10B981',
          'Cancelled': '#EF4444',
          'Overdue': '#EF4444'
        }
        const statusColor = statusColors[value] || '#6B7280'
        return (
          <span 
            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
            style={{ 
              backgroundColor: `${statusColor}20`,
              color: statusColor,
              border: `1px solid ${statusColor}40`
            }}
          >
            {value || 'N/A'}
          </span>
        )
      }
    },
    {
      key: 'startDate',
      label: 'Start Date',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatDate(value)}
        </div>
      )
    },
    {
      key: 'endDate',
      label: 'End Date',
      sortable: true,
      render: (value) => (
        <div className="font-medium text-gray-900">
          {formatDate(value)}
        </div>
      )
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Resource Allocation List', path: '/projects/resource-allocations/list', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Resource Allocation List</h1>
          <p>Create, view, and manage all resource allocations</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn-primary"
            onClick={handleAddNew}
          >
            <Plus size={16} />
            Add New Allocation
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <Users size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+12%</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalAllocations || 0}</div>
          <div className="metric-title">Total Allocations</div>
          <div className="metric-subtitle">vs last month</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <TrendingUp size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+8%</span>
            </div>
          </div>
          <div className="metric-value">{stats.active || 0}</div>
          <div className="metric-title">Active</div>
          <div className="metric-subtitle">Currently active</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <Clock size={16} />
            </div>
            <div className="metric-trend positive">
              <TrendingUp size={12} />
              <span>+5%</span>
            </div>
          </div>
          <div className="metric-value">{stats.planned || 0}</div>
          <div className="metric-title">Planned</div>
          <div className="metric-subtitle">Scheduled allocations</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <AlertCircle size={16} />
            </div>
            <div className="metric-trend negative">
              <TrendingUp size={12} />
              <span>+3%</span>
            </div>
          </div>
          <div className="metric-value">{stats.overdue || 0}</div>
          <div className="metric-title">Overdue</div>
          <div className="metric-subtitle">Need attention</div>
        </div>
      </div>

      {/* Resource Allocation List Table */}
      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={handleEdit}
          onView={handleView}
          onDelete={handleDelete}
          onRevisionHistory={handleRevisionHistory}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search resource allocations..."
          emptyMessage="No resource allocations found. Create your first allocation to get started!"
          noCard={true}
        />
      </div>

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        title="Delete Resource Allocation"
        message={`Are you sure you want to delete this resource allocation? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      {/* Revision History Modal */}
      <RevisionHistoryModal
        isOpen={showRevisionHistory}
        onClose={() => setShowRevisionHistory(false)}
        data={selectedResourceAllocationData}
        title="Resource Allocation Revision History"
      />
    </div>
  )
}

export default ResourceAllocationList

